import { useState, useEffect } from 'react';

// Diverse invalid credentials responses
const invalidCredentialsResponses = [
  "Hmm, those credentials don't look right. Let's try again.",
  "Not quite right... Double-check and give it another shot?",
  "Oops, that doesn't seem to be working. Mind trying again?",
  "I don't recognize those credentials. Want to try once more?",
  "That combination isn't clicking. Let's give it another go.",
];

export const getRandomInvalidMessage = () => {
  const randomIndex = Math.floor(Math.random() * invalidCredentialsResponses.length);
  return invalidCredentialsResponses[randomIndex];
};

// Multilingual welcome messages
export const welcomeMessages = [
  { lang: 'English', text: "Welcome back! Ready to continue your learning journey?" },
  { lang: 'Spanish', text: "¡Bienvenido de nuevo! ¿Listo para continuar tu viaje de aprendizaje?" },
  { lang: 'Japanese', text: "おかえりなさい！学習の旅を続ける準備はできていますか？" },
  { lang: 'Chinese', text: "欢迎回来！准备好继续你的学习之旅了吗？" },
  { lang: 'Korean', text: "다시 오신 것을 환영합니다! 학습 여정을 계속할 준비가 되셨나요?" },
  { lang: 'Farsi', text: "خوش آمدید! آماده ادامه سفر یادگیری خود هستید؟" },
  { lang: 'German', text: "Willkommen zurück! Bereit, deine Lernreise fortzusetzen?" }
];

export function useSignInChat({
  stage,
  attemptCount = 0,
  onStreamComplete,
  onStreamStart,
  isStreaming = true,
  onCharacterTyped
}: {
  stage: 'welcome' | 'invalidCredentials' | 'thirdAttempt';
  attemptCount?: number;
  onStreamComplete?: () => void;
  onStreamStart?: () => void;
  isStreaming?: boolean;
  onCharacterTyped?: () => void;
}) {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [currentInvalidMessage, setCurrentInvalidMessage] = useState('');
  const [currentWelcomeIndex, setCurrentWelcomeIndex] = useState(0);

  // Generate message based on stage
  const getMessage = (currentStage: typeof stage) => {
    if (currentStage === 'welcome') {
      return welcomeMessages[currentWelcomeIndex].text;
    } else if (currentStage === 'invalidCredentials') {
      return currentInvalidMessage || getRandomInvalidMessage();
    } else if (currentStage === 'thirdAttempt') {
      return "Having trouble? Try ";
    }
    return '';
  };

  // Update invalid message when stage changes OR attemptCount changes
  useEffect(() => {
    if (stage === 'invalidCredentials') {
      setCurrentInvalidMessage(getRandomInvalidMessage());
    }
  }, [stage, attemptCount]); // Now triggers on each new attempt

  // Welcome message cycling and streaming - all in one effect
  useEffect(() => {
    if (stage !== 'welcome') return;

    const message = welcomeMessages[currentWelcomeIndex].text;
    
    // Notify that streaming is starting
    if (onStreamStart) onStreamStart();
    
    // Stream in the message
    setDisplayedText('');
    setIsComplete(false);
    let charIndex = 0;

    const streamInInterval = setInterval(() => {
      if (charIndex < message.length) {
        setDisplayedText(message.slice(0, charIndex + 1));
        charIndex++;
        if (onCharacterTyped) onCharacterTyped();
      } else {
        clearInterval(streamInInterval);
        setIsComplete(true);
        if (onStreamComplete) onStreamComplete();
      }
    }, 30);

    // After 8 seconds total, cycle to next message
    const cycleTimeout = setTimeout(() => {
      setCurrentWelcomeIndex((prev) => (prev + 1) % welcomeMessages.length);
    }, 8000);

    return () => {
      clearInterval(streamInInterval);
      clearTimeout(cycleTimeout);
    };
  }, [stage, currentWelcomeIndex]); // Removed onCharacterTyped and onStreamComplete from dependencies

  // Handle non-welcome stages
  useEffect(() => {
    if (stage === 'welcome') return;
    
    const message = getMessage(stage);
    
    if (!message) {
      setDisplayedText('');
      setIsComplete(false);
      return;
    }

    if (!isStreaming) {
      setDisplayedText(message);
      setIsComplete(true);
      if (onStreamComplete) onStreamComplete();
      return;
    }

    // Notify that streaming is starting
    if (onStreamStart) onStreamStart();

    setDisplayedText('');
    setIsComplete(false);
    let currentIndex = 0;

    const intervalId = setInterval(() => {
      if (currentIndex < message.length) {
        setDisplayedText(message.slice(0, currentIndex + 1));
        currentIndex++;
        if (onCharacterTyped) onCharacterTyped();
      } else {
        clearInterval(intervalId);
        setIsComplete(true);
        if (onStreamComplete) onStreamComplete();
      }
    }, 30);

    return () => clearInterval(intervalId);
  }, [stage, isStreaming, currentInvalidMessage]); // Removed callbacks from dependencies

  return {
    displayedText,
    isComplete,
    message: getMessage(stage)
  };
}

